import {
    CartItemSpaTreatmentDetailDto,
    CreateCartItemCommand,
    useCreateCartItemMutation,
    useDeleteCartItemByIdMutation,
    useDeleteCartItemsByIdMutation,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { useCart } from 'store/cart-slice/hooks'
import { catchErrorByStatus, ErrorField, handleError } from 'helpers/errors'
import { v4 as uuidv4 } from 'uuid'
import { useAppSelector } from 'store/hooks'
import { useEffect } from 'react'

export const useSpaCart = () => {
    const { language } = useApp()
    const { refetchCart, cartCreatorType } = useCart()

    const { spa: cartItems } = useAppSelector((state) => state.cart.items)

    const [postCartItem, postCartItemStatus] = useCreateCartItemMutation()
    const {
        isLoading: isSubmitLoading,
        isSuccess: isSubmitSuccess,
        isError: isSubmitError,
        error: submitError,
    } = postCartItemStatus

    const [deleteCartItem, deleteCartItemStatus] =
        useDeleteCartItemByIdMutation()
    const {
        isLoading: isDeleteItemLoading,
        isSuccess: isDeleteItemSuccess,
        isError: isDeleteItemError,
        error: deleteItemError,
    } = deleteCartItemStatus

    const [deleteCartItems, deleteCartItemsStatus] =
        useDeleteCartItemsByIdMutation()
    const {
        isLoading: isDeleteSpaLoading,
        isSuccess: isDeleteSpaSuccess,
        isError: isDeleteSpaError,
        error: deleteSpaError,
    } = deleteCartItemsStatus

    const isEditLoading = isDeleteItemLoading || isSubmitLoading
    const isEditSuccess = isDeleteItemSuccess && isSubmitSuccess
    const isEditError = isDeleteItemError || isSubmitError

    useEffect(() => {
        // catch global errors
        // TODO: check if it is possible to put this into the the rtk middleware
        const currentError = deleteItemError || deleteSpaError || submitError
        currentError && catchErrorByStatus({ error: currentError })
    }, [submitError, deleteItemError, deleteSpaError])

    const addItemToCart = async (
        variantId: string,
        startDate: string,
        endDate: string,
        variant: number
    ) => {
        const detail: CartItemSpaTreatmentDetailDto = {
            startDate,
            endDate,
            variant,
        }
        const cartItemCommandData: CreateCartItemCommand = {
            cartItemId: uuidv4(),
            productId: variantId,
            // TODO: fix types for const declared globally, use it here
            productType: 'spa_treatment',
            quantity: 1,
            detail,
        }

        try {
            handleError(
                await postCartItem({
                    createCartItemCommand: { ...cartItemCommandData },
                    cartCreatorType,
                    _locale: language,
                })
            )
            refetchCart(true)
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const editItemInCart = async (
        cartItemId: string,
        variantId: string,
        startDate: string,
        endDate: string,
        variant: number
    ) => {
        const detail: CartItemSpaTreatmentDetailDto = {
            startDate,
            endDate,
            variant,
        }
        const cartItemCommandData: CreateCartItemCommand = {
            cartItemId: uuidv4(),
            productId: variantId,
            // TODO: fix types for const declared globally, use it here
            productType: 'spa_treatment',
            quantity: 1,
            detail,
        }

        // TODO: reverse actions
        // - first try to add new item, next delete the old one
        // - adding should go first because it can be conflicted with other buyers
        // - ! reversing action causes unidentified problems with refetching cart

        // first: delete the old treatment
        try {
            handleError(
                await deleteCartItem({
                    cartItemId,
                    _locale: language,
                    cartCreatorType,
                })
            )
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
        // second: add newly configured (=edited) treatment
        try {
            handleError(
                await postCartItem({
                    createCartItemCommand: { ...cartItemCommandData },
                    cartCreatorType,
                    _locale: language,
                })
            )
            refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const deleteItemFromCart = async (cartItemId: string) => {
        try {
            handleError(
                await deleteCartItem({
                    cartItemId,
                    _locale: language,
                    cartCreatorType,
                })
            )
            refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const deleteSpaFromCart = async () => {
        const cartItemsIds = cartItems.map(({ cartItemId }) => cartItemId || '')
        const filteredCartItemsIds = cartItemsIds.filter((id) => id !== '')

        try {
            handleError(
                await deleteCartItems({
                    _locale: language,
                    cartCreatorType,
                    deleteCartItemsByIdCommand: {
                        cartItemUuids: [...filteredCartItemsIds],
                    },
                })
            )
            refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    return {
        cartItems,
        addItemToCart,
        editItemInCart,
        deleteItemFromCart,
        deleteSpaFromCart,
        submitError,
        isSubmitLoading,
        isSubmitSuccess,
        isSubmitError,
        isDeleteItemLoading,
        isDeleteItemSuccess,
        isDeleteItemError,
        isDeleteSpaLoading,
        isDeleteSpaSuccess,
        isDeleteSpaError,
        isEditLoading,
        isEditSuccess,
        isEditError,
    }
}
